import { useEffect, useRef, useState } from 'react'

import { useSelector } from 'react-redux'

import { useAuth } from '@/core/hooks/useAuth'
import { RootState } from '@/core/store'
import {
	shouldCheckSchedulesFeature,
	getRedirectUrl,
	isAlreadyOnRedirectTarget,
} from '@/core/utils/domain'

/**
 * Hook to guard the schedules application based on user's schedules feature
 *
 * This hook monitors the Redux user state and redirects users who don't have
 * the schedules feature to the legacy application (domain without "new." prefix).
 *
 * The hook only activates on specific domains (new.my.* and localhost:4200).
 *
 * @returns {object} - shouldRender: whether to render app, isChecking: whether still checking
 */
export const useSchedulesFeatureGuard = () => {
	const { user, isAuthenticated } = useSelector(
		(state: RootState) => state.auth
	)
	const { isInitialized, logout } = useAuth()

	const [isChecking, setIsChecking] = useState(true)
	const hasRedirected = useRef(false)
	const checkStartTime = useRef<number | null>(null)

	useEffect(() => {
		// Only check if we're on a domain that requires the guard
		if (!shouldCheckSchedulesFeature()) {
			setIsChecking(false)
			return
		}

		// Set up interval to periodically re-check if stuck waiting for user
		let timeoutId: NodeJS.Timeout | null = null
		if (isAuthenticated && !user && isInitialized) {
			timeoutId = setTimeout(() => {
				// Force a re-render to trigger timeout check
				setIsChecking((prev) => prev)
			}, 1000)
		}

		// Don't check until auth is initialized
		if (!isInitialized) {
			setIsChecking(true)
			return
		}

		// If user is not authenticated, allow rendering (ProtectedRoute will handle redirect to login)
		if (!isAuthenticated) {
			setIsChecking(false)
			return
		}

		// If authenticated but user not loaded yet, keep checking
		// But add a safety timeout - if stuck here for too long, allow rendering
		if (!user) {
			// Start timeout tracking if not started
			if (checkStartTime.current === null) {
				checkStartTime.current = Date.now()
			}

			// If we've been waiting more than 5 seconds, give up and render
			// This prevents infinite stuck state if user data fails to load
			const waitTime = Date.now() - checkStartTime.current
			if (waitTime > 5000) {
				console.warn(
					'[SchedulesFeatureGuard] User data not loaded after 5s, allowing render'
				)
				setIsChecking(false)
				return
			}

			setIsChecking(true)
			return
		}

		// Reset timeout tracking once user is loaded
		checkStartTime.current = null

		// Safety check: ensure account is loaded
		if (!user.account) {
			// Account not loaded yet, keep checking
			// In production, we might want to log this or handle differently
			console.warn('[SchedulesFeatureGuard] User loaded but account is missing')
			setIsChecking(true)
			return
		}

		// Check if user has schedules feature
		const hasFeature = user.account.hasSchedulesFeature()

		if (!hasFeature) {
			// User doesn't have feature - need to redirect

			// Prevent redirect loops
			if (isAlreadyOnRedirectTarget()) {
				console.warn(
					'[SchedulesFeatureGuard] Already on redirect target, preventing loop'
				)
				setIsChecking(false)
				return
			}

			// Prevent double redirects
			if (hasRedirected.current) {
				return
			}

			hasRedirected.current = true

			// Perform redirect
			const redirectUrl = getRedirectUrl()
			console.log('redirecting user to legacy app', {
				id: user.account.id,
				name: user.account.name,
				email: user.email,
				redirectUrl,
			})

			//Before redirecting, logout the user so the user is not keeping in an infinite loop
			logout()

			// Use window.location.href for full page navigation (not React Router)
			// This ensures we leave the new app entirely
			window.location.href = redirectUrl

			// Don't render while redirecting
			setIsChecking(true)
			return
		}

		// User has the feature - allow rendering
		setIsChecking(false)

		// Cleanup timeout if set
		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId)
			}
		}
	}, [user, isAuthenticated, isInitialized])

	return {
		isChecking,
	}
}
